import { getMockedUsageData } from 'lib/browserStorage';
import { sleep } from 'lib/utilityFunctions';
import AuthService from 'lib/authService';
import { getCurrentWeekStartDate, secondsToMinutes } from 'lib/DatetimeManager';
import { getWeeklyTarget } from 'lib/usageUtils';
import { logRumError } from 'lib/DDRum';
import { fetchUsageTargets as fetchUsageTargetsApi } from 'a/api/usageTargets';
import { fetchUsage } from 'a/api/usage';
import { USAGE_ACTION_TYPES } from 'a/reducers/usageReducer';
import { API_REQUEST_STATUS } from 'lib/constants';
import { getSelectedProduct } from 'a/selectors/appSessionSelectors';
import { getAuthDataGrade, getSchoolYearStart } from 'a/selectors/persistStateSelectors';

const USAGE_FETCH_COOLDOWN_MS = 1 * 60 * 1000; // 1 minute

/**
 * Dispatches an action to mark usage progress as seen for a selected product.
 * @param {object} params
 * @param {number} params.lastShownUsageTime - The usage time (in minutes) last shown to the student
 */
export const markUsageProgressSeen =
  ({ lastShownUsageTime }) =>
    (dispatch, getState) => {
      const state = getState();
      const selectedProduct = getSelectedProduct(state);

      dispatch({
        type: USAGE_ACTION_TYPES.MARK_USAGE_PROGRESS_SEEN,
        payload: { product: selectedProduct, lastShownUsageTime },
      });
    };

/**
 * Thunk action that fetches usage data for the current week for a single product.
 * Dispatches request, success, or error actions based on the outcome.
 * When mock data is active, bypasses the API and always dispatches success with the mock value.
 * @param {string} ampId - The student's AMP ID
 * @param {string} product - The product key (e.g. 'reading', 'lectura')
 * @param {string} startDate - The start date of the current week in "YYYY-MM-DD" format
 */
const fetchWeeklyUsage = (ampId, product, startDate) => async (dispatch) => {
  dispatch({ type: USAGE_ACTION_TYPES.GET_USAGE_REQUEST, payload: { product, startDate } });

  const mockData = getMockedUsageData();
  if (mockData?.[product]) {
    await sleep(500);
    const { weeklyUsageTime = 0, lastShownUsageTime = 0 } = mockData[product];
    dispatch({ type: USAGE_ACTION_TYPES.GET_USAGE_SUCCESS, payload: { product, time: weeklyUsageTime } });
    dispatch(markUsageProgressSeen({ lastShownUsageTime }));
    return;
  }

  try {
    const usageData = await fetchUsage({ studentAmpId: ampId, product, startDate });
    const weeklyUsageTime = Object.values(usageData || {}).reduce((acc, val) => acc + secondsToMinutes(val), 0);
    dispatch({
      type: USAGE_ACTION_TYPES.GET_USAGE_SUCCESS,
      payload: { product, time: weeklyUsageTime },
    });
  } catch {
    dispatch({ type: USAGE_ACTION_TYPES.GET_USAGE_ERROR, payload: { product } });
  }
};

/**
 * Thunk action that fetches current week usage data for the given products in parallel.
 * Each product's request is independent — one failure does not affect the other.
 * Resolves immediately if the student AMP ID is unavailable or no products are provided.
 * @param {string[]} products - Array of product keys to fetch usage for (e.g. ['reading', 'lectura'])
 * @param {object} [options]
 * @param {boolean} [options.cooldown=false] - When true, skips any product whose last successful fetch is within USAGE_FETCH_COOLDOWN_MS.
 * @returns {Promise} Resolves when all requests have settled
 */
export const fetchProductsWeeklyUsage =
  (products, { cooldown = false } = {}) =>
    async (dispatch, getState) => {
      const ampId = AuthService.getUserBk();

      if (!ampId || !products?.length) {
        return Promise.resolve();
      }

      const state = getState();
      const schoolYearStart = getSchoolYearStart(state);
      const startDate = getCurrentWeekStartDate(schoolYearStart);
      const shouldUseCooldown = cooldown && !getMockedUsageData();
      const now = Date.now();

      const productsToFetch = !shouldUseCooldown
        ? products
        : products.filter((product) => {
          const { lastFetchedAt, weekKey, status } = state.usage[product] || {};
          if (status === API_REQUEST_STATUS.LOADING) return false;
          if (weekKey !== startDate) return true;
          return !lastFetchedAt || now - lastFetchedAt >= USAGE_FETCH_COOLDOWN_MS;
        });

      if (!productsToFetch.length) {
        return Promise.resolve();
      }

      return Promise.allSettled(
        productsToFetch.map((product) => dispatch(fetchWeeklyUsage(ampId, product, startDate))),
      );
    };

/**
 * Thunk action that fetches the canonical weekly usage targets and stores the one matching the
 * student's grade. Dispatches request, success, or error actions based on the outcome - failures are
 * swallowed so the caller never rejects.
 * Targets don't change within a session, so this fetches once - dispatching again while a request is
 * in flight or after it succeeded is a no-op, while a previous failure can be retried.
 * @returns {Promise} Resolves when the request has settled
 */
export const fetchUsageTargets = () => async (dispatch, getState) => {
  const state = getState();
  const { status } = state.usage?.target || {};

  if ([API_REQUEST_STATUS.LOADING, API_REQUEST_STATUS.SUCCESS].includes(status)) {
    return;
  }

  const studentGrade = getAuthDataGrade(state);

  dispatch({ type: USAGE_ACTION_TYPES.GET_USAGE_TARGETS_REQUEST });

  try {
    const usageTargets = await fetchUsageTargetsApi();
    const weeklyTarget = getWeeklyTarget(usageTargets, studentGrade);

    if (!weeklyTarget) {
      logRumError('RTU: no usable weekly target for the user grade', { grade: studentGrade || 'empty' });
    }

    dispatch({
      type: USAGE_ACTION_TYPES.GET_USAGE_TARGETS_SUCCESS,
      payload: { weeklyTarget },
    });
  } catch {
    dispatch({ type: USAGE_ACTION_TYPES.GET_USAGE_TARGETS_ERROR });
  }
};
